import { motion } from "framer-motion";

export const Dot = ({ tone = "cyan", className = "" }) => {
    const colors = {
        cyan: "bg-[#00D9FF]",
        green: "bg-emerald-400",
        amber: "bg-amber-400",
        dim: "bg-[#B8C7D9]/50",
    };
    return (
        <span
            className={`inline-block h-1.5 w-1.5 rounded-full vm-pulse-dot ${colors[tone]} ${className}`}
            aria-hidden="true"
        />
    );
};

const TONE_STYLES = {
    live: "border-emerald-400/40 text-emerald-300",
    beta: "border-[#00D9FF]/40 text-[#00D9FF]",
    dev: "border-[#1688FF]/40 text-[#1688FF]",
    concept: "border-[#B8C7D9]/30 text-[#B8C7D9]/80",
};

export const Tag = ({ children, tone = "beta" }) => (
    <span
        className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[9px] tracking-[0.2em] ${TONE_STYLES[tone] || TONE_STYLES.beta}`}
    >
        {children}
    </span>
);

export const Panel = ({ title, right, children, className = "", testid }) => (
    <div data-testid={testid} className={`vm-panel relative overflow-hidden ${className}`}>
        {(title || right) && (
            <div className="flex items-center justify-between border-b border-[rgba(0,217,255,0.12)] px-4 py-2">
                <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 bg-[#00D9FF]/70" aria-hidden="true" />
                    <span className="vm-micro">{title}</span>
                </div>
                {right && <div className="vm-micro-dim">{right}</div>}
            </div>
        )}
        <div className="relative">{children}</div>
    </div>
);

export const SectionHead = ({ index, title, sub, className = "" }) => (
    <Reveal className={`mb-10 ${className}`}>
        <div className="vm-micro mb-3 flex items-center gap-3">
            <span>{index}</span>
            <span className="h-px w-10 bg-[#00D9FF]/30" aria-hidden="true" />
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-[#F5F9FF] sm:text-3xl lg:text-4xl">
            {title}
        </h2>
        {sub && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#B8C7D9] sm:text-base">{sub}</p>}
    </Reveal>
);

export const Reveal = ({ children, delay = 0, className = "", y = 24 }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
        {children}
    </motion.div>
);

export const Marquee = ({ items }) => (
    <div className="relative overflow-hidden border-y border-[rgba(0,217,255,0.12)] bg-[#080F18]/60 py-3" aria-hidden="true">
        <div className="vm-marquee flex w-max whitespace-nowrap">
            {[0, 1].map((copy) => (
                <div key={copy} className="flex">
                    {items.map((item, i) => (
                        <span key={`${copy}-${i}`} className="mx-8 flex items-center gap-3 font-mono text-[10px] tracking-[0.28em] text-[#B8C7D9]/70">
                            <span className="text-[#00D9FF]">▸</span> {item}
                        </span>
                    ))}
                </div>
            ))}
        </div>
    </div>
);

export const KV = ({ k, v, tone = "default" }) => (
    <div className="flex items-baseline justify-between gap-4 py-1 font-mono text-xs">
        <span className="tracking-[0.14em] text-[#B8C7D9]/70">{k}</span>
        <span className={tone === "ok" ? "text-emerald-300" : tone === "cyan" ? "text-[#00D9FF]" : "text-[#F5F9FF]/90"}>
            {v}
        </span>
    </div>
);

export const FlowStack = ({ steps, onSelect, activeId, compact = false }) => (
    <div className="flex flex-col">
        {steps.map((step, i) => {
            const isActive = activeId === step.id;
            const Wrapper = onSelect ? "button" : "div";
            return (
                <div key={step.id}>
                    <Wrapper
                        data-testid={`flow-${step.id}`}
                        onClick={onSelect ? () => onSelect(step.id) : undefined}
                        className={`group flex w-full items-center gap-4 border px-4 text-left transition-colors duration-300 ${
                            compact ? "py-2.5" : "py-4"
                        } ${
                            isActive
                                ? "border-[#00D9FF]/50 bg-[#00D9FF]/[0.06]"
                                : "border-[rgba(0,217,255,0.12)] bg-[#080F18]/50 hover:border-[#00D9FF]/30 hover:bg-[#00D9FF]/[0.03]"
                        }`}
                    >
                        <span className={`font-mono text-[10px] tracking-[0.2em] ${isActive ? "text-[#00D9FF]" : "text-[#B8C7D9]/50"}`}>
                            {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className={`font-mono text-xs tracking-[0.18em] sm:text-sm ${isActive ? "text-[#F5F9FF]" : "text-[#B8C7D9] group-hover:text-[#F5F9FF]"}`}>
                            {step.name}
                        </span>
                        {isActive && <Dot className="ml-auto" />}
                    </Wrapper>
                    {i < steps.length - 1 && (
                        <div className="flex justify-start pl-8" aria-hidden="true">
                            <svg width="2" height={compact ? 14 : 22} className="overflow-visible">
                                <line x1="1" y1="0" x2="1" y2={compact ? 14 : 22} stroke="rgba(0,217,255,0.35)" strokeWidth="1" className="vm-dash" />
                            </svg>
                        </div>
                    )}
                </div>
            );
        })}
    </div>
);
